// App composition root (D-25..D-38).
// HMR singleton guard via globalThis.__app__ ??= new App() (D-31).
// main.tsx is a one-liner: `void app.init()`.
import { createElement, useSyncExternalStore } from "react"
import { createRoot, type Root } from "react-dom/client"
import { AuthPrimitive, AuthSignedInEvent, AuthSignedOutEvent } from "./primitives/auth"
import { EventBus } from "./primitives/eventBus"
import { Logger } from "./primitives/log"
import { Store } from "./primitives/store"
import { createSupabaseAnon } from "./primitives/supabase"
import { Theme } from "./primitives/theme"

export type BootState =
  | { kind: "pre-auth" }
  | { kind: "authed-loading" }
  | { kind: "syncing" }
  | { kind: "ready" }
  | { kind: "error"; error: Error }

export class App {
  readonly eventBus = new EventBus()
  readonly log = new Logger({ name: "web" })
  readonly supabase = createSupabaseAnon()
  readonly auth = new AuthPrimitive(this.supabase, this.eventBus)
  readonly store = new Store()
  readonly theme = new Theme(this.supabase, this.auth)

  // mutable nullable (Phase 2 lights up):
  // sync: SyncProxy | null = null
  // wsClient: WsClient | null = null

  #root: Root | null = null
  #bootState: BootState = { kind: "pre-auth" }
  #bootStateListeners = new Set<() => void>()

  async init(): Promise<void> {
    // Subscribe to auth events first (D-29).
    this.eventBus.on(AuthSignedInEvent, async (e) => {
      await this.boot(e.userId)
    })
    this.eventBus.on(AuthSignedOutEvent, async () => {
      await this.stop()
    })

    // Mount React root. Use createElement (not JSX) so this file can be .ts.
    const el = document.getElementById("root")
    if (!el) throw new Error("Missing #root in index.html")
    this.#root = createRoot(el)
    const { AppRoot } = await import("./AppRoot")
    this.#root.render(createElement(AppRoot, { app: this }))

    // Start auth — emits AuthSignedInEvent if session exists.
    await this.auth.start()
  }

  private async boot(_userId: string): Promise<void> {
    this.#setBootState({ kind: "authed-loading" })
    try {
      await this.theme.hydrateFromServer()
      this.#setBootState({ kind: "syncing" })
      // Phase 2: this.sync = await createSyncProxy(this); this.wsClient = createWsClient(...)
      this.#setBootState({ kind: "ready" })
    } catch (err) {
      this.#setBootState({
        kind: "error",
        error: err instanceof Error ? err : new Error(String(err)),
      })
    }
  }

  private async stop(): Promise<void> {
    // Phase 2: tear down sync, ws, group event subs.
    this.#setBootState({ kind: "pre-auth" })
  }

  useBootState(): BootState {
    return useSyncExternalStore(
      (cb) => {
        this.#bootStateListeners.add(cb)
        return () => {
          this.#bootStateListeners.delete(cb)
        }
      },
      () => this.#bootState,
      () => this.#bootState,
    )
  }

  #setBootState(s: BootState): void {
    this.#bootState = s
    for (const cb of this.#bootStateListeners) cb()
  }
}

// HMR singleton guard (D-31).
declare global {
  var __app__: App | undefined
}
function getOrCreateApp(): App {
  if (!globalThis.__app__) {
    globalThis.__app__ = new App()
  }
  return globalThis.__app__
}
export const app: App = getOrCreateApp()
if (import.meta.hot) import.meta.hot.accept()
